import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';

import api from '../../services/api';
import {
  ComprasEUnmatchedAuthor,
  ProjectCostCompany,
  ProjectCostManagementSnapshot,
  ProjectCostRole,
} from '../../types';
import { buildFriendlyApiError } from '../../utils/apiError';
import { formatComprasEHours } from './comprasEExportPanel';
import {
  buildComprasEUnmatchedCollaboratorPayload,
  inferIsThirdPartyFromCompanyName,
  unmatchedAuthorKey,
} from './comprasEUnmatchedCollaborator';

type Props = {
  show: boolean;
  authors: ComprasEUnmatchedAuthor[];
  onHide: () => void;
  onRegistered: () => Promise<void>;
  showAlert: (params: { title: string; message: string; variant?: 'success' | 'warning' | 'danger' | 'info' }) => void;
};

const ComprasERegisterUnmatchedCollaboratorModal: React.FC<Props> = ({
  show,
  authors,
  onHide,
  onRegistered,
  showAlert,
}) => {
  const [loadingSnapshot, setLoadingSnapshot] = useState(false);
  const [saving, setSaving] = useState(false);
  const [companies, setCompanies] = useState<ProjectCostCompany[]>([]);
  const [roles, setRoles] = useState<ProjectCostRole[]>([]);
  const [selectedKey, setSelectedKey] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [roleId, setRoleId] = useState('');
  const [hourlyRateOverride, setHourlyRateOverride] = useState('');
  const [isThirdParty, setIsThirdParty] = useState(false);

  const activeCompanies = useMemo(() => companies.filter((item) => item.is_active), [companies]);
  const activeRoles = useMemo(() => roles.filter((item) => item.is_active), [roles]);
  const selectedAuthor = useMemo(
    () => authors.find((author) => unmatchedAuthorKey(author) === selectedKey) || authors[0] || null,
    [authors, selectedKey],
  );

  useEffect(() => {
    if (!show) return;
    const first = authors[0];
    setSelectedKey(first ? unmatchedAuthorKey(first) : '');
    setName(first?.jira_display_name || '');
    setEmail('');
    setCompanyId('');
    setRoleId('');
    setHourlyRateOverride('');
    setIsThirdParty(false);
    setLoadingSnapshot(true);
    void api.get<ProjectCostManagementSnapshot>('/api/projects/costs/snapshot')
      .then(({ data }) => {
        setCompanies(data.companies || []);
        setRoles(data.roles || []);
      })
      .catch((err: unknown) => {
        const friendly = buildFriendlyApiError(err, {
          defaultMessage: 'Não foi possível carregar empresas e funções de custo.',
        });
        showAlert({ title: 'Erro', message: friendly.message, variant: 'danger' });
      })
      .finally(() => setLoadingSnapshot(false));
  }, [show, showAlert]);

  useEffect(() => {
    if (!selectedAuthor) return;
    setName(selectedAuthor.jira_display_name || '');
  }, [selectedAuthor]);

  const handleCompanyChange = (nextCompanyId: string) => {
    const selectedCompany = activeCompanies.find((item) => String(item.id) === nextCompanyId);
    setCompanyId(nextCompanyId);
    setIsThirdParty(inferIsThirdPartyFromCompanyName(selectedCompany?.name));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedAuthor) return;
    const built = buildComprasEUnmatchedCollaboratorPayload(selectedAuthor, {
      name,
      email,
      companyId,
      roleId,
      hourlyRateOverride,
      isThirdParty,
    });
    if (!built.ok) {
      if (built.error === 'missing_account_id') {
        showAlert({
          title: 'Cadastro incompleto',
          message: 'Este apontamento não veio com accountId do Jira. Cadastre em Custos de Projetos com o vínculo Jira preenchido.',
          variant: 'warning',
        });
      } else if (built.error === 'invalid_override') {
        showAlert({
          title: 'Valor inválido',
          message: 'Informe um override válido em R$/h.',
          variant: 'warning',
        });
      }
      return;
    }
    setSaving(true);
    try {
      await api.post('/api/projects/costs/collaborators', built.payload);
      showAlert({
        title: 'Colaborador cadastrado',
        message: `${selectedAuthor.jira_display_name} foi vinculado ao Jira. O recorte será atualizado para incluir o custo.`,
        variant: 'success',
      });
      onHide();
      await onRegistered();
    } catch (err: unknown) {
      const friendly = buildFriendlyApiError(err, {
        defaultMessage: 'Não foi possível cadastrar o colaborador.',
      });
      showAlert({ title: 'Erro', message: friendly.message, variant: 'danger' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>Cadastrar colaborador</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info" className="py-2">
            O vínculo certo é o <strong>accountId do Jira</strong>. Sem empresa/função (ou override),
            a hora entra no HH Real e o custo continua zerado.
          </Alert>
          {loadingSnapshot ? (
            <div className="text-center py-3">
              <Spinner animation="border" size="sm" className="me-2" />
              Carregando empresas e funções…
            </div>
          ) : (
            <Row className="g-2">
              {authors.length > 1 && (
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Autor sem cadastro</Form.Label>
                    <Form.Select
                      value={selectedKey}
                      onChange={(event) => setSelectedKey(event.target.value)}
                    >
                      {authors.map((author) => (
                        <option key={unmatchedAuthorKey(author)} value={unmatchedAuthorKey(author)}>
                          {author.jira_display_name} ({formatComprasEHours(author.hours)} HH)
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              )}
              {selectedAuthor && (
                <Col md={12}>
                  <div className="text-muted small">
                    {formatComprasEHours(selectedAuthor.hours)} HH em {selectedAuthor.worklog_count} worklog(s).
                    AccountId: {selectedAuthor.jira_account_id || '—'}
                  </div>
                </Col>
              )}
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Nome</Form.Label>
                  <Form.Control
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.Label>E-mail</Form.Label>
                  <Form.Control
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder="Opcional"
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Empresa</Form.Label>
                  <Form.Select
                    value={companyId}
                    onChange={(event) => handleCompanyChange(event.target.value)}
                    required
                  >
                    <option value="">Selecione</option>
                    {activeCompanies.map((company) => (
                      <option key={company.id} value={company.id}>{company.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Função</Form.Label>
                  <Form.Select
                    value={roleId}
                    onChange={(event) => setRoleId(event.target.value)}
                  >
                    <option value="">Selecione</option>
                    {activeRoles.map((role) => (
                      <option key={role.id} value={role.id}>{role.name}</option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Use a mesma função das tarifas da empresa, ou informe um override abaixo.
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Taxa override (R$/h)</Form.Label>
                  <Form.Control
                    type="text"
                    inputMode="decimal"
                    placeholder="Ex.: 180,00"
                    value={hourlyRateOverride}
                    onChange={(event) => setHourlyRateOverride(event.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Check
                  id="compras-e-unmatched-third-party"
                  label="Colaborador terceiro"
                  checked={isThirdParty}
                  onChange={(event) => setIsThirdParty(event.target.checked)}
                />
                <Form.Text className="text-muted">
                  Empresa com “Petronect” no nome entra como próprio; as demais, como terceiro.
                </Form.Text>
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide} disabled={saving}>Cancelar</Button>
          <Button type="submit" disabled={saving || loadingSnapshot || !companyId}>
            {saving ? 'Cadastrando…' : 'Cadastrar e atualizar'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ComprasERegisterUnmatchedCollaboratorModal;
